import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { LancamentosDeCaixaService } from '../../../services/lancamentos-de-caixa.service';
import { finalize } from 'rxjs';
import { SpinnerService } from '../../../services/spinner.service';
import { ToastService } from '../../../services/toast.service';
import { SeveridadeToast } from '../../../models/enums/severidade-toast.enum';
import { PlanoDeContasService } from '../../../services/plano-de-contas.service';
import { Router } from '@angular/router';
import { Icones } from '../../../shared/variaveisDeRotas';
import { JwtHelper } from '../../../helpers/jwtDecode.helper';
import { UsuarioAllProperties } from '../../../models/usuarioAllProperties.model';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-adicionar-lancamento-caixa',
  templateUrl: './adicionar-lancamento-caixa.component.html',
  styleUrls: ['./adicionar-lancamento-caixa.component.scss']
})
export class AdicionarLancamentoCaixaComponent implements OnInit {
  @Output() tituloChange = new EventEmitter<string>();
  @Output() iconeChange = new EventEmitter<string>();

  lancamentoForm: FormGroup = new FormGroup({
    tipo: new FormControl(0, Validators.required),
    periodo: new FormControl<Date | null>(null, Validators.required),
    valor: new FormControl(null, [Validators.required, Validators.min(0)]),
    descricao: new FormControl('', Validators.required),
    contaContabilId: new FormControl(null, Validators.required),
    usuarioId: new FormControl(''),
  });

  activeIndex: number = 0;
  contaContabilOptions: any[] = [];
  contaContabilValorAtual: any;
  groupByNatureza: any;
  userId: string;
  usuario: UsuarioAllProperties;
  dataCriacaoUsuario: Date;

  steps = [
    { label: 'Tipo de Lançamento' },
    { label: 'Detalhes do Lançamento' }
  ];

  constructor(
    private lancamentoDeCaixaService: LancamentosDeCaixaService,
    private planoDeContasService: PlanoDeContasService,
    private spinnerService: SpinnerService,
    private jwtHelper: JwtHelper,
    private toastService: ToastService,
    private router: Router,
    private authService: AuthService
  ) {
    this.userId = this.jwtHelper.getUserIdFromToken();
  }

  ngOnInit(): void {
    this.tituloChange.emit('Lançamentos de Caixa');
    this.iconeChange.emit(Icones.adicaoAmarelo);
  }

  nextStep(): void {
    if (this.lancamentoForm.get('tipo')?.valid) {
      this.activeIndex = 1;
      this.lancamentoForm.get('usuarioId')?.setValue(this.userId);
      this.listaPorTipoLancamento();
      this.retornaTodasPropriedadesUsuario(this.userId);
    }
  }

  listaPorTipoLancamento(): void {
    this.spinnerService.show('Carregando...');
    this.lancamentoDeCaixaService.listaTipoLancamentoPreenchido(this.lancamentoForm.get('tipo')?.value)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: (data: any[]) => {

          const contaContabilIds = data.map(item => item.id);

          if (contaContabilIds.length > 0) {
            this.contaContabilValorAtual = { contaContabilIds };
            this.carregaPlanoDeContas();
          } else {
            this.toastService.setMensagemToast('Nenhuma conta contábil encontrada.', SeveridadeToast.Warn);
          }
        },
        error: (error) => {
          console.error('Erro na API:', error);
          this.toastService.setMensagemToast('Erro ao carregar Lançamento caixa.', SeveridadeToast.Error);
        }
      });
  }

  get dataCriacaoUsuarioISO(): string {
    return this.dataCriacaoUsuario
      ? this.dataCriacaoUsuario.toISOString().split('T')[0]
      : '';
  }

  retornaTodasPropriedadesUsuario(userId: string) {
    this.spinnerService.show('Carregando dados do usuário...');
    
    this.authService.obterUsuarioAllPropertiesPorId(userId)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: (res) => {
          this.usuario = res;
          this.retornaDataCriacaoUsuario(this.usuario)
        },
        error: (e) => {
          this.toastService.setMensagemToast(`Erro ao obter usuário.`, SeveridadeToast.Error);
        }
      })
  }

  retornaDataCriacaoUsuario(usuario: UsuarioAllProperties) {
    const fullDate = new Date(usuario.dataVerificacaoEmail);
    this.dataCriacaoUsuario = new Date(fullDate.getFullYear(), fullDate.getMonth(), fullDate.getDate());
  }

  carregaPlanoDeContas(): void {
    if (!this.contaContabilValorAtual || !Array.isArray(this.contaContabilValorAtual.contaContabilIds)) {
      console.error('contaContabilValorAtual está indefinido ou não contém contaContabilIds válidos.');
      this.toastService.setMensagemToast('Erro ao carregar plano de contas', SeveridadeToast.Error);
      return;
    }

    this.spinnerService.show('Carregando plano de contas...');
    
    this.planoDeContasService.getContasContabeis(this.userId)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe({
        next: (res: any[]) => {
          this.contaContabilOptions = res.filter(contaContabil =>
            this.contaContabilValorAtual.contaContabilIds.includes(contaContabil.id)
          );
        },
        error: (e) => {
          console.error('Erro ao carregar plano de contas:', e);
          this.toastService.setMensagemToast('Erro ao carregar plano de contas', SeveridadeToast.Error);
        }
      });
  }


  prevStep(): void {
    this.activeIndex = 0;
  }

  onSubmit(): void {
    if (this.lancamentoForm.valid) {
      this.spinnerService.show('Salvando lançamento...');
      
      this.lancamentoForm.get("contaContabilId").setValue(this.lancamentoForm.value.contaContabilId.id);
      this.lancamentoForm.get("periodo").setValue(this.transformaData(this.lancamentoForm.value.periodo));
      
      this.lancamentoDeCaixaService.criarLancamentoDeCaixa(this.lancamentoForm.getRawValue())
        .pipe(finalize(() => this.spinnerService.hide()))
        .subscribe({
          next: (res) => {
            this.toastService.setMensagemToast('Lançamento caixa cadastrado com sucesso!', SeveridadeToast.Success);
            this.router.navigate(['listar-lancamento-caixa']);
          },
          error: (e) => {
            this.toastService.setMensagemToast(`Erro ao adicionar lançamento caixa: ${e.error}`, SeveridadeToast.Error);
          }
        });
      
      this.lancamentoForm.reset();
      this.activeIndex = 0;
    }
  }

  transformaData(data: Date | null): number {
    if (!data) {
      throw new Error('Data é obrigatória');
    }
    
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    
    return Number(`${ano}${mes}${dia}`);
  }
}